import type { Dayjs } from "dayjs"

import type { CommContact, CommRecord } from "./record-contacts.ts"

export type RecordView = "all" | "queue"
export type ChannelFilter = "" | "email" | "sms"

export const QUEUE_REASONS = [
  "quiet_hours",
  "send_window",
  "legal_hours",
  "scheduled",
  "capped",
  "throttled",
  "retry",
  "paused",
  "due",
  "sending"
] as const

export interface RecordFilters {
  q: string
  channel: ChannelFilter
  status: string[]
  reason: string[]
  source: string[]
  sender: string[]
  range: [Dayjs, Dayjs] | null
  /** A `contactKey`; only a customer, subcontractor or supplier can be one. */
  recipient: string | null
}

export const EMPTY_FILTERS: RecordFilters = {
  q: "",
  channel: "",
  status: [],
  reason: [],
  source: [],
  sender: [],
  range: null,
  recipient: null
}

export const DEFAULT_ORDERING: Record<RecordView, string> = {
  all: "-sent",
  queue: "next_attempt"
}

export const RECORD_PAGE_SIZE = 25

/** Contacts a message can be filtered by: a lead's own row is the record itself. */
export const recipientChoices = (contacts: CommContact[]) =>
  contacts.filter((contact) => contact.role !== "lead")

/** Filters that narrow the list beyond the record itself. */
export const activeFilterCount = (filters: RecordFilters, view: RecordView) =>
  [
    filters.q.trim(),
    filters.channel,
    view === "all" ? filters.status.length : filters.reason.length,
    filters.source.length,
    filters.sender.length,
    filters.range,
    filters.recipient
  ].filter(Boolean).length

/**
 * The messages endpoint's query for one record's list: every filter and
 * the order go to the server, so they cover every message, not a page.
 */
export const recordQuery = (
  record: Pick<CommRecord, "type" | "id">,
  view: RecordView,
  filters: RecordFilters,
  ordering: string,
  page: number
) => {
  const query: Record<string, string | number> = {
    record_type: record.type,
    record_id: record.id,
    ordering,
    page,
    page_size: RECORD_PAGE_SIZE
  }
  if (view === "queue") query.view = "queue"
  const q = filters.q.trim()
  if (q) query.q = q
  if (filters.channel) query.channel = filters.channel
  if (view === "all" && filters.status.length)
    query.status = filters.status.join(",")
  if (view === "queue" && filters.reason.length)
    query.reason = filters.reason.join(",")
  if (filters.source.length) query.source = filters.source.join(",")
  if (filters.sender.length) query.sender = filters.sender.join(",")
  if (filters.range) {
    query.created_after = filters.range[0].startOf("day").toISOString()
    query.created_before = filters.range[1].endOf("day").toISOString()
  }
  if (filters.recipient) {
    const [type, id] = filters.recipient.split(":")
    if (type && id && type !== "lead") query[type] = id
  }
  return query
}

/** The record's queue, unfiltered: what the Queue tab's count says. */
export const queueCountQuery = (record: Pick<CommRecord, "type" | "id">) => ({
  record_type: record.type,
  record_id: record.id,
  view: "queue",
  page: 1,
  page_size: 1
})
